import React from 'react';

import cn from '@/utils/cn';

import { calloutClass, toneText } from './tone';
import { AnalyzedSection, Status } from './types';

const BREAKDOWN_ORDER: Status[] = ['changed', 'online_missing', 'no_change', 'not_compared', 'no_print_match'];
const BREAKDOWN_LABELS: Record<Status, string> = {
  changed: 'changed',
  no_change: 'unchanged',
  no_print_match: 'no print match',
  not_compared: 'not compared',
  online_missing: 'not online yet',
};

// eslint-disable-next-line max-len
const CHECK_PATH = 'M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z';
// eslint-disable-next-line max-len
const ALERT_PATH = 'M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z';

const StatusIcon = ({ path }: { path: string }) => (
  <svg aria-hidden="true" className="tw-h-6 tw-w-6" fill="currentColor" viewBox="0 0 20 20">
    <path clipRule="evenodd" d={path} fillRule="evenodd" />
  </svg>
);

// The hero result: a calm success "all clear" when nothing changed, or a warning "needs
// action" with the count when sections need review. Announced to screen readers.
const SummaryBanner = ({ results }: { results: AnalyzedSection[] }) => {
  const counts = results.reduce<Partial<Record<Status, number>>>((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});
  const changed = counts.changed || 0;
  // A print section with no online home (e.g. a Lesson Opener) needs action just like a change.
  const actionable = changed + (counts.online_missing || 0);
  const compared = (counts.no_change || 0) + changed;
  const allClear = actionable === 0;
  const tone = allClear ? 'success' : 'warning';

  const headline = allClear
    ? 'Nothing to review'
    : `${actionable} section${actionable === 1 ? '' : 's'} to review`;
  const subtext = allClear
    ? `All ${compared} compared section${compared === 1 ? '' : 's'} match the print copy word-for-word.`
    : 'Only the changed sections below need review — the rest match the print copy.';
  const breakdown = BREAKDOWN_ORDER
    .filter(status => counts[status])
    .map(status => `${counts[status]} ${BREAKDOWN_LABELS[status]}`)
    .join(' · ');

  return (
    <div className={cn(calloutClass(tone), 'tw-rounded-lg tw-p-4', toneText[tone])} role="status">
      <div className="tw-flex tw-items-start tw-gap-3">
        <span className="tw-mt-0.5 tw-flex-shrink-0">
          <StatusIcon path={allClear ? CHECK_PATH : ALERT_PATH} />
        </span>
        <div>
          <p className="tw-m-0 tw-text-xl tw-font-bold">{headline}</p>
          <p className="tw-m-0 tw-mt-0.5 tw-text-sm">{subtext}</p>
          {breakdown && <p className="tw-m-0 tw-mt-1 tw-text-sm tw-text-spa-secondary">{breakdown}</p>}
        </div>
      </div>
    </div>
  );
};

export default SummaryBanner;
